
import { toast } from 'sonner';
import { fetchArxivLicenseFromUrl, extractArxivId } from './arxiv-license';
import { ArxivLicense } from '@/types/license';
import { supabase } from '@/integrations/supabase/client';

export interface ArxivMetadata {
  title: string;
  abstract: string;
  authors: string[];
  categories: string[];
  category?: string; // Add category for backward compatibility
  published: string;
  doi?: string;
  // License information for compliance checking
  license?: ArxivLicense;
}

export interface ArxivValidationResult {
  isValid: boolean;
  metadata?: ArxivMetadata;
  errors?: string[];
  // License validation results
  licenseValid?: boolean;
  licenseCompliant?: boolean;
}

/**
 * Validates an arXiv URL and fetches metadata from arXiv API if valid.
 * Also fetches and validates license information for compliance checking.
 * @param url The arXiv URL to validate
 * @param checkLicense Whether to fetch and validate license information (default: true)
 * @returns Validation result with metadata and license information if valid
 */
export async function validateAndFetchArxivMetadata(
  url: string, 
  checkLicense: boolean = true
): Promise<ArxivValidationResult> {
  try {
    // Basic URL validation - accept both arxiv.org and www.arxiv.org
    if (!url || !(url.includes('arxiv.org') || url.includes('www.arxiv.org'))) {
      return { 
        isValid: false, 
        errors: ['URL must be from arxiv.org'] 
      };
    }

    // Extract arXiv ID from URL using the license service function
    const arxivId = extractArxivId(url);
    if (!arxivId) {
      return { 
        isValid: false, 
        errors: ['Could not extract valid arXiv ID from URL. Format should be: https://arxiv.org/abs/XXXX.XXXXX'] 
      };
    }
    
    // Fetch metadata from arXiv API via edge function to avoid CORS
    const response = await supabase.functions.invoke('fetch-arxiv-paper-metadata', {
      body: { arxivId }
    });
    
    // Handle rate limiting
    if (response.error?.message?.includes('429') || response.data?.error?.includes('Too many requests')) {
      toast.error('Please wait a moment before fetching more papers.');
      return { 
        isValid: false, 
        errors: ['Rate limit exceeded. Please wait a moment before trying again.'] 
      };
    }
    
    if (response.error) {
      return { 
        isValid: false, 
        errors: [`Error fetching from arXiv API: ${response.error.message}`] 
      };
    }
    
    const data = response.data?.xmlData;
    
    // Parse XML response
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(data, "text/xml");
    
    // Check if we got an error response from arXiv
    const entries = xmlDoc.getElementsByTagName('entry');
    if (entries.length === 0) {
      return { 
        isValid: false, 
        errors: ['No paper found with that ID on arXiv'] 
      };
    }
    
    // Extract metadata
    const entry = entries[0];
    const title = entry.getElementsByTagName('title')[0]?.textContent?.trim() || '';
    const abstract = entry.getElementsByTagName('summary')[0]?.textContent?.trim() || '';
    const published = entry.getElementsByTagName('published')[0]?.textContent || '';
    
    // Extract authors
    const authorNodes = entry.getElementsByTagName('author');
    const authors: string[] = [];
    for (let i = 0; i < authorNodes.length; i++) {
      const name = authorNodes[i].getElementsByTagName('name')[0]?.textContent;
      if (name) authors.push(name);
    }
    
    // Extract categories
    const categoryNodes = entry.getElementsByTagName('category');
    const categories: string[] = [];
    for (let i = 0; i < categoryNodes.length; i++) {
      const category = categoryNodes[i].getAttribute('term');
      if (category) categories.push(category);
    }
    
    // Extract DOI and license from multiple sources
    let doi: string | undefined;
    let licenseText: string | undefined;
    
    // Check for license in arxiv:license tags
    const licenseNodes = entry.getElementsByTagNameNS('http://arxiv.org/schemas/atom', 'license');
    if (licenseNodes.length > 0 && !licenseText) {
      licenseText = licenseNodes[0].textContent?.trim();
    }
    
    // Check for license in rights tags
    const rightsNodes = entry.getElementsByTagName('rights');
    if (rightsNodes.length > 0 && !licenseText) {
      const rightsText = rightsNodes[0].textContent?.trim();
      if (rightsText && rightsText.includes('http')) {
        licenseText = rightsText;
      }
    }
    
    // Extract from links
    const links = entry.getElementsByTagName('link');
    for (let i = 0; i < links.length; i++) {
      const href = links[i].getAttribute('href') || '';
      const rel = links[i].getAttribute('rel') || '';
      
      // Check for DOI
      if (href.includes('doi.org') && !doi) {
        doi = href;
        break;
      }
    }
    
    // Fetch license information if requested
    let license: ArxivLicense | undefined;
    let licenseValid = true;
    let licenseCompliant = true;
    
    if (checkLicense) {
      try {
        const licenseResult = await fetchArxivLicenseFromUrl(url);
        if (licenseResult.success && licenseResult.license) {
          license = licenseResult.license;
          licenseCompliant = licenseResult.license.isCompliant;
        } else {
          // License fetch failed - log warning but don't fail validation
          console.warn(`License fetch failed for ${url}:`, licenseResult.error);
          licenseValid = false;
        }
      } catch (error) {
        console.warn(`License validation error for ${url}:`, error);
        licenseValid = false;
      }
    }

    const metadata: ArxivMetadata = {
      title,
      abstract,
      authors,
      categories,
      category: categories.length > 0 ? categories[0] : '',
      published,
      doi,
      license
    };
    
    // If license checking is enabled and the license is non-compliant, fail validation
    if (checkLicense && licenseValid && !licenseCompliant) {
      return {
        isValid: false,
        metadata,
        licenseValid,
        licenseCompliant,
        errors: [
          `This paper uses a ${license?.name || 'non-compliant'} license, which restricts commercial use and derivative works. Papers with these restrictions cannot be uploaded to maintain open academic sharing.`
        ]
      };
    }
    
    return { 
      isValid: true, 
      metadata,
      licenseValid,
      licenseCompliant
    };
    
  } catch (error) {
    console.error('Error validating arXiv URL:', error);
    return { 
      isValid: false, 
      errors: ['An unexpected error occurred while validating the arXiv URL'] 
    };
  }
}
